import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { fetchEmployeeEngagements, createEmployeeEngagement, updateEmployeeEngagement, deleteEmployeeEngagement } from '../services/api';
import Autocomplete from '@mui/material/Autocomplete';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { fetchProjects, fetchClients, fetchProcesses } from '../services/api';

const AssignEmployee = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employee: '',
    project: '',
    process: '',
    startDate: '',
    endDate: '',
    allocation: '',
  });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [projects, setProjects] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [selectedEmployeeEngagements, setSelectedEmployeeEngagements] = useState([]);
  const [processTimeline, setProcessTimeline] = useState({ start: '', end: '' });
  const [allocationWarning, setAllocationWarning] = useState('');
  const [dateWarning, setDateWarning] = useState('');

  useEffect(() => {
    // Fetch projects, processes, and all engagements for validation
    const loadData = async () => {
      try {
        const [projectsData, processesData, engagementsData] = await Promise.all([
          fetchProjects(),
          fetchProcesses(),
          fetchEmployeeEngagements()
        ]);
        
        // Flatten the grouped projects structure for easier handling
        const flattenedProjects = projectsData.flatMap(clientGroup => 
          clientGroup.projects.map(project => ({
            ...project,
            client_name: clientGroup.client_name
          }))
        );
        
        setProjects(flattenedProjects);
        setProcesses(processesData);
        setEngagements(engagementsData);
        
        console.log('Projects:', flattenedProjects);
        console.log('Processes:', processesData);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (employeeSearch.length === 0) {
      setEmployeeOptions([]);
      return;
    }
    setLoadingEmployees(true);
    fetch(`/api/users?search=${encodeURIComponent(employeeSearch)}`)
      .then(res => res.json())
      .then(data => {
        setEmployeeOptions(data);
        setLoadingEmployees(false);
        console.log('Employee search results:', data);
      })
      .catch(() => setLoadingEmployees(false));
  }, [employeeSearch]);

  // When employee is selected, show their current/future engagements
  useEffect(() => {
    if (!formData.employee) {
      setSelectedEmployeeEngagements([]);
      return;
    }
    const empEngs = engagements.filter(e => (e.employeeName === formData.employee || e.employee_name === formData.employee));
    setSelectedEmployeeEngagements(empEngs);
  }, [formData.employee, engagements]);

  // When process is selected, show its timeline
  useEffect(() => {
    if (!formData.process) {
      setProcessTimeline({ start: '', end: '' });
      return;
    }
    const proc = processes.find(p => p.id === formData.process || p.id === Number(formData.process));
    if (proc) {
      setProcessTimeline({ start: proc.start_date, end: proc.end_date });
    } else {
      setProcessTimeline({ start: '', end: '' });
    }
  }, [formData.process, processes]);

  // Validate allocation and dates
  useEffect(() => {
    // Allocation validation
    if (formData.employee && formData.allocation) {
      const total = selectedEmployeeEngagements.reduce((sum, e) => sum + (Number(e.hoursPerWeek) || 0), 0) + Number(formData.allocation);
      if (total > 100) {
        setAllocationWarning('Total allocation for this employee exceeds 100%. Please adjust.');
      } else {
        setAllocationWarning('');
      }
    } else {
      setAllocationWarning('');
    }
    // Date validation
    if (formData.startDate && formData.endDate && processTimeline.start && processTimeline.end) {
      if (formData.startDate < processTimeline.start || formData.endDate > processTimeline.end) {
        setDateWarning('Engagement dates must be within the process timeline.');
      } else {
        setDateWarning('');
      }
    } else {
      setDateWarning('');
    }
  }, [formData, selectedEmployeeEngagements, processTimeline]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (allocationWarning || dateWarning) return;
    try {
      const payload = {
        employeeName: formData.employee,
        projectId: formData.project,
        processId: formData.process,
        startDate: formData.startDate,
        endDate: formData.endDate,
        hoursPerWeek: formData.allocation,
        status: 'Active'
      };
      await createEmployeeEngagement(payload);
      navigate('/employee-engagement/list', { replace: true });
    } catch (error) {
      alert('Failed to save employee engagement.');
    }
  };

  // Filter processes by selected project
  const filteredProcesses = processes.filter(p => p.project_id === formData.project || p.project_id === Number(formData.project));

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Assign Employee to Project
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={employeeOptions}
                getOptionLabel={(option) =>
                  option.full_name && option.email
                    ? `${option.full_name} (${option.user_role || ''}) - ${option.email}`
                    : ''
                }
                loading={loadingEmployees}
                onInputChange={(_, value) => setEmployeeSearch(value)}
                onChange={(_, value) => setFormData({ ...formData, employee: value ? value.full_name : '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Employee (search by name or email)" required />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  label="Project"
                  required
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.project_name} - {project.client_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Process</InputLabel>
                <Select
                  name="process"
                  value={formData.process}
                  onChange={handleChange}
                  label="Process"
                  required
                  disabled={!formData.project}
                >
                  {filteredProcesses.map((proc) => (
                    <MenuItem key={proc.id} value={proc.id}>
                      {proc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Allocation (%)"
                name="allocation"
                value={formData.allocation}
                onChange={handleChange}
                required
                inputProps={{ min: 0, max: 100 }}
                error={!!allocationWarning}
                helperText={allocationWarning}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: processTimeline.start, max: processTimeline.end }}
                error={!!dateWarning}
                helperText={dateWarning}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: processTimeline.start, max: processTimeline.end }}
                error={!!dateWarning}
                helperText={dateWarning}
              />
            </Grid>
            {/* Show employee's current engagements */}
            {formData.employee && selectedEmployeeEngagements.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Current Engagements for {formData.employee}:</Typography>
                  <ul>
                    {selectedEmployeeEngagements.map((e, idx) => (
                      <li key={idx}>
                        {e.projectId || e.project_id} / {e.process_id} | {e.startDate || e.start_date} - {e.endDate || e.end_date} | {e.hoursPerWeek || e.allocation}%
                      </li>
                    ))}
                  </ul>
                </Box>
              </Grid>
            )}
            {/* Show process timeline */}
            {formData.process && processTimeline.start && processTimeline.end && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Process Timeline:</Typography>
                  <Typography>{processTimeline.start} to {processTimeline.end}</Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/employee-engagement')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!!allocationWarning || !!dateWarning}
                >
                  Assign Employee
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

const EditEmployeeEngagement = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    employee: '',
    project: '',
    role: '',
    startDate: '',
    endDate: '',
    allocation: '',
    skills: [],
  });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  useEffect(() => {
    if (employeeSearch.length === 0) {
      setEmployeeOptions([]);
      return;
    }
    setLoadingEmployees(true);
    fetch(`/api/users?search=${encodeURIComponent(employeeSearch)}`)
      .then(res => res.json())
      .then(data => {
        setEmployeeOptions(data);
        setLoadingEmployees(false);
      })
      .catch(() => setLoadingEmployees(false));
  }, [employeeSearch]);

  const projects = [
    { id: 1, name: 'Website Redesign' },
    { id: 2, name: 'Mobile App Development' },
    { id: 3, name: 'Cloud Migration' },
  ];

  const roles = [
    'Project Manager',
    'Developer',
    'Designer',
    'QA Engineer',
    'Business Analyst',
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const engagementId = window.location.pathname.split('/').pop();
      
      // Map frontend form fields to backend expected fields
      const payload = {
        employeeName: formData.employee,
        projectId: formData.project,
        role: formData.role,
        startDate: formData.startDate,
        endDate: formData.endDate,
        hoursPerWeek: formData.allocation,
        status: 'Active' // Default status
      };
      
      await updateEmployeeEngagement(engagementId, payload);
      navigate('/employee-engagement/list', { replace: true });
    } catch (error) {
      alert('Failed to update employee engagement.');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Edit Employee Engagement
      </Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={employeeOptions}
                getOptionLabel={(option) =>
                  option.full_name && option.email
                    ? `${option.full_name} (${option.user_role || ''}) - ${option.email}`
                    : ''
                }
                loading={loadingEmployees}
                onInputChange={(_, value) => setEmployeeSearch(value)}
                onChange={(_, value) => setFormData({ ...formData, employee: value ? value.full_name : '' })}
                renderInput={(params) => (
                  <TextField {...params} label="Employee (search by name or email)" required />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={employeeOptions.find(e => e.full_name === formData.employee) || null}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  name="project"
                  value={formData.project}
                  onChange={handleChange}
                  label="Project"
                  required
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  label="Role"
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Allocation (%)"
                name="allocation"
                value={formData.allocation}
                onChange={handleChange}
                required
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/employee-engagement')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Update Engagement
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

const EmployeeEngagementList = () => {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedClient, setExpandedClient] = useState(false);
  const [expandedProject, setExpandedProject] = useState(false);
  const [expandedProcess, setExpandedProcess] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEngagement, setSelectedEngagement] = useState(null);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const [engagementsData, projectsData, clientsData, processesData] = await Promise.all([
          fetchEmployeeEngagements(),
          fetchProjects(),
          fetchClients(),
          fetchProcesses()
        ]);
        
        // Flatten the grouped projects structure for easier handling
        const flattenedProjects = projectsData.flatMap(clientGroup => 
          clientGroup.projects.map(project => ({
            ...project,
            client_name: clientGroup.client_name
          }))
        );
        
        setEngagements(engagementsData);
        setProjects(flattenedProjects);
        setClients(clientsData);
        setProcesses(processesData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // Group by client > project > process
  const grouped = {};
  projects.forEach(project => {
    const clientId = project.client_id;
    if (!grouped[clientId]) grouped[clientId] = {};
    grouped[clientId][project.id] = {};
    processes.filter(proc => proc.project_id === project.id).forEach(proc => {
      grouped[clientId][project.id][proc.id] = [];
    });
  });
  engagements.forEach(eng => {
    const project = projects.find(p => p.id === eng.projectId || p.id === eng.project_id);
    const process = processes.find(proc => proc.id === eng.process_id || proc.id === eng.processId);
    if (project && process) {
      const clientId = project.client_id;
      if (!grouped[clientId]) grouped[clientId] = {};
      if (!grouped[clientId][project.id]) grouped[clientId][project.id] = {};
      if (!grouped[clientId][project.id][process.id]) grouped[clientId][project.id][process.id] = [];
      grouped[clientId][project.id][process.id].push(eng);
    }
  });

  const handleDeleteClick = (engagement) => {
    setSelectedEngagement(engagement);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteEmployeeEngagement(selectedEngagement.id);
      setEngagements(engagements.filter(eng => eng.id !== selectedEngagement.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      alert('Failed to delete employee engagement.');
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '';
    const parts = name.trim().split(' ');
    return parts.length > 1
      ? (parts[0][0] || '') + (parts[1][0] || '')
      : (parts[0][0] || '');
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading employee engagements...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Employee Engagements</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/employee-engagement/assign')}
        >
          Assign Employee
        </Button>
      </Box>
      {clients.map(client => (
        <Accordion
          key={client.id}
          expanded={expandedClient === client.id}
          onChange={() => setExpandedClient(expandedClient === client.id ? false : client.id)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{client.company_name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {projects.filter(p => p.client_id === client.id).map(project => (
              <Accordion
                key={project.id}
                expanded={expandedProject === project.id}
                onChange={() => setExpandedProject(expandedProject === project.id ? false : project.id)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">{project.project_name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {processes.filter(proc => proc.project_id === project.id).map(process => (
                    <Accordion
                      key={process.id}
                      expanded={expandedProcess === process.id}
                      onChange={() => setExpandedProcess(expandedProcess === process.id ? false : process.id)}
                      sx={{ mb: 2 }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body1">Process: {process.name}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Allocation (%)</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(grouped[client.id][project.id][process.id] || []).map(engagement => (
                                <TableRow key={engagement.id}>
                                  <TableCell>{engagement.employeeName || engagement.employee_name}</TableCell>
                                  <TableCell>{engagement.role}</TableCell>
                                  <TableCell>{engagement.startDate || engagement.start_date}</TableCell>
                                  <TableCell>{engagement.endDate || engagement.end_date}</TableCell>
                                  <TableCell>{engagement.hoursPerWeek || engagement.allocation}</TableCell>
                                  <TableCell>{engagement.status}</TableCell>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => navigate(`/employee-engagement/edit/${engagement.id}`)}>
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(engagement)}>
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
      {/* Delete dialog remains unchanged */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Employee Engagement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the engagement for "{selectedEngagement?.employee}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const EmployeeEngagement = () => {
  return (
    <Routes>
      <Route path="/" element={<EmployeeEngagementList />} />
      <Route path="/assign" element={<AssignEmployee />} />
      <Route path="/edit/:id" element={<EditEmployeeEngagement />} />
    </Routes>
  );
};

export default EmployeeEngagement;